export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

export interface ILogger {
  debug: (message: string, ...args: unknown[]) => void;
  info: (message: string, ...args: unknown[]) => void;
  warn: (message: string, ...args: unknown[]) => void;
  error: (message: string, ...args: unknown[]) => void;
}

export interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  prefix?: string;
}

const logLevelOrder = {
  [LogLevel.DEBUG]: 0,
  [LogLevel.INFO]: 1,
  [LogLevel.WARN]: 2,
  [LogLevel.ERROR]: 3,
};

export function createLogger(config: LoggerConfig): ILogger {
  const shouldLog = (level: LogLevel): boolean => {
    if (!config.enabled) return false;
    return logLevelOrder[level] >= logLevelOrder[config.level];
  };

  const formatMessage = (level: LogLevel, message: string): string => {
    const timestamp = new Date().toISOString();
    const prefix = config.prefix ? `[${config.prefix}]` : '';
    return `[${timestamp}] ${prefix} [${level.toUpperCase()}] ${message}`;
  };

  return {
    debug: (message: string, ...args: unknown[]) => {
      if (shouldLog(LogLevel.DEBUG)) {
        // eslint-disable-next-line no-console
        console.debug(formatMessage(LogLevel.DEBUG, message), ...args);
      }
    },
    info: (message: string, ...args: unknown[]) => {
      if (shouldLog(LogLevel.INFO)) {
        // eslint-disable-next-line no-console
        console.info(formatMessage(LogLevel.INFO, message), ...args);
      }
    },
    warn: (message: string, ...args: unknown[]) => {
      if (shouldLog(LogLevel.WARN)) {
        // eslint-disable-next-line no-console
        console.warn(formatMessage(LogLevel.WARN, message), ...args);
      }
    },
    error: (message: string, ...args: unknown[]) => {
      if (shouldLog(LogLevel.ERROR)) {
        // eslint-disable-next-line no-console
        console.error(formatMessage(LogLevel.ERROR, message), ...args);
      }
    },
  };
}

// Default logger for development
export const defaultLogger = createLogger({
  enabled: true,
  level: LogLevel.DEBUG,
  prefix: 'WYZ-Connect',
});
